import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AGENTS, AGENT_TEMPLATES, PORTCOS, getIntegrationsForPortco, WORKSTREAM_INTEGRATIONS } from '../mock/platformData';
import { usePortCo } from '../context/PortCoContext';
import type { WorkstreamType, AgentStatus, WorkstreamAgent } from '../types';
import {
  Activity,
  Cpu,
  Sparkles,
  CheckCircle2,
  SlidersHorizontal,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  AlertCircle,
  Play,
  Loader2,
  FileText,
  DollarSign,
  Clock,
  Building2,
  ChevronRight,
  ChevronDown,
  Plug,
  ChevronUp,
} from 'lucide-react';
import { rcmApi, type RcmClaimsResponse, type RcmDashboardResponse, type AgentConfig } from '../api/rcmApi';
import { ErrorBoundary } from '../components/ErrorBoundary';

export interface WorkstreamDetailProps {
  workstream?: WorkstreamType;
}

// Workstream color fallback mapping for compatibility
export const WORKSTREAM_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  marketing: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  referrals: { bg: 'bg-mantis-50', text: 'text-mantis-600', border: 'border-mantis-200' },
  intake: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  scheduling: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
  rcm: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  hr: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  finance: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  'clinical-ops': { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
  orchestrator: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
};

// Simplified status styling: Mantis for Active, Redwood for Processing, Amber for Idle
const getStatusBadgeStyle = (status: AgentStatus) => {
  switch (status) {
    case 'active':
      return {
        badge: 'bg-mantis-50 text-mantis-600 border-mantis-200',
        dot: 'bg-mantis-500',
        label: 'Active',
      };
    case 'processing':
      return {
        badge: 'bg-redwood-50 text-redwood-500 border-redwood-200',
        dot: 'bg-redwood-500',
        label: 'Processing',
      };
    case 'idle':
    case 'paused':
    default:
      return {
        badge: 'bg-amber-50 text-amber-600 border-amber-200',
        dot: 'bg-amber-500',
        label: status === 'paused' ? 'Paused' : 'Idle',
      };
  }
};

const getPriorityBadgeStyle = (priority: string) => {
  switch (priority) {
    case 'high':
    case 'critical':
      return 'bg-redwood-50 text-redwood-500 border-redwood-200';
    case 'medium':
      return 'bg-amber-50 text-amber-600 border-amber-200';
    case 'low':
    default:
      return 'bg-mantis-50 text-mantis-600 border-mantis-200';
  }
};

// Aggregate agents by workstream type (for "All Businesses" view)
function aggregateByWorkstream(agents: WorkstreamAgent[]) {
  const grouped = new Map<string, { template: WorkstreamAgent; instances: WorkstreamAgent[] }>();
  for (const agent of agents) {
    const key = agent.workstream;
    if (!grouped.has(key)) {
      grouped.set(key, { template: agent, instances: [agent] });
    } else {
      grouped.get(key)!.instances.push(agent);
    }
  }

  return Array.from(grouped.entries()).map(([ws, { template, instances }]) => {
    const totalActions = instances.reduce((s, a) => s + (a.actionsToday || 0), 0);
    const avgSuccess = instances.length
      ? (instances.reduce((s, a) => s + (a.successRate || 0), 0) / instances.length).toFixed(1)
      : '0';
    const anyProcessing = instances.some((a) => a.status === 'processing');
    const allIdle = instances.every((a) => a.status === 'idle' || a.status === 'paused');
    const status: AgentStatus = anyProcessing ? 'processing' : allIdle ? 'idle' : 'active';

    return {
      ...template,
      id: `agg_${ws}`,
      portcoId: 'all',
      actionsToday: totalActions,
      successRate: parseFloat(avgSuccess),
      status,
      portcoCount: instances.length,
      instances,
    };
  });
}


function getMockRecentActions(workstream: string, agentName: string) {
  const wsLabel = workstream ? workstream.replace('-', ' ') : 'operational';
  return [
    {
      timestamp: '12 mins ago',
      description: `Evaluated automated ${wsLabel} workflow rules & queue priorities`,
      status: 'Completed',
    },
    {
      timestamp: '45 mins ago',
      description: `Generated ${agentName} performance & optimization recommendation`,
      status: 'Approved',
    },
    {
      timestamp: '2 hours ago',
      description: `Processed batch operational telemetry for ${wsLabel} pipeline`,
      status: 'Completed',
    },
    {
      timestamp: '4 hours ago',
      description: 'Executed scheduled health checks & integration sync',
      status: 'Completed',
    },
  ];
}

export const WorkstreamDetail: React.FC<WorkstreamDetailProps> = ({ workstream: propWorkstream }) => {
  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);
  const [runningAgents, setRunningAgents] = useState<Record<string, boolean>>({});
  const [runStatus, setRunStatus] = useState<Record<string, string>>({});
  const [agentEnabled, setAgentEnabled] = useState<Record<string, boolean>>({});
  const [configLoaded, setConfigLoaded] = useState(false);

  const handleRunAgent = async (agentId: string, portcoId: string, workstream: string) => {
    setRunningAgents((prev) => ({ ...prev, [agentId]: true }));
    setRunStatus((prev) => ({ ...prev, [agentId]: '' }));

    try {
      const res = await rcmApi.runAgent(workstream, portcoId || 'portco_apex');
      if (res?.mode === 'queued' && res.runId) {
        setRunStatus((prev) => ({
          ...prev,
          [agentId]: `✓ Run queued (${res.runId}) — executing via Service Bus`,
        }));
      } else if (res?.recommendationsGenerated !== undefined) {
        setRunStatus((prev) => ({
          ...prev,
          [agentId]: `✓ Run complete — ${res.itemsAnalyzed} items analyzed, ${res.recommendationsGenerated} recommendations (${res.modelUsed}, ${res.tokensUsed} tokens)`,
        }));
      } else {
        setRunStatus((prev) => ({ ...prev, [agentId]: '✓ Run completed' }));
      }
    } catch (e: any) {
      setRunStatus((prev) => ({
        ...prev,
        [agentId]: `✗ ${e?.message || 'Agent run failed'}`,
      }));
    } finally {
      setRunningAgents((prev) => ({ ...prev, [agentId]: false }));
    }
  };

  const handleToggleAgent = async (agentId: string, portcoId: string, workstream: string, next: boolean) => {
    const key = `${portcoId}|${workstream}`;
    setAgentEnabled((prev) => ({ ...prev, [key]: next })); // optimistic
    try {
      await rcmApi.setAgentConfig(portcoId || 'portco_apex', workstream, next);
      setRunStatus((prev) => ({ ...prev, [agentId]: next ? '✓ Agent enabled' : 'Agent disabled — runs blocked' }));
    } catch (e: any) {
      setAgentEnabled((prev) => ({ ...prev, [key]: !next })); // rollback
      setRunStatus((prev) => ({ ...prev, [agentId]: `✗ ${e?.message || 'Config update failed'}` }));
    }
  };

  const { workstreamId } = useParams<{ workstreamId?: string }>();
  const navigate = useNavigate();
  const { selectedPortco, selectedPortcoName } = usePortCo();

  const [selectedWorkstream, setSelectedWorkstream] = useState<string>(
    propWorkstream || workstreamId || 'all'
  );

  // Sync URL param changes to state (e.g., navigating /workstreams/rcm -> /workstreams/hr)
  useEffect(() => {
    if (workstreamId && workstreamId !== selectedWorkstream) {
      setSelectedWorkstream(workstreamId);
    }
  }, [workstreamId]);

  // Load per-tenant agent on/off switches from the live config API
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const configs = await rcmApi.getAgentConfigs(selectedPortco);
        if (cancelled) return;
        const map: Record<string, boolean> = {};
        for (const c of configs) map[`${c.portcoId}|${c.workstream}`] = c.enabled;
        setAgentEnabled(map);
      } catch {
        if (!cancelled) setAgentEnabled({});
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPortco]);

  // Commercial entitlement: only show cards for workstreams this business has
  // purchased. The config API omits non-entitled agents (deny-by-default), so
  // a card only renders when its portco|workstream key comes back from the API.
  const toCatalogWs = (ws: string) => (ws === 'clinical-ops' ? 'clinical_doc' : ws);
  // Live-first: build the agent list from the live config registry (the source of
  // truth for purchased/deployed agents) instead of the hardcoded demo list, so
  // newly onboarded firms show their agents automatically. Falls back to the
  // demo list only if the registry is unreachable.
  const safeAgents = useMemo<any[]>(() => {
    if (!configLoaded) return [];
    const enabledKeys = Object.keys(agentEnabled);
    if (enabledKeys.length === 0) {
      return (AGENTS || []).filter(
        (a) => agentEnabled[`${a.portcoId}|${toCatalogWs(a.workstream)}`] !== undefined
      );
    }
    const uiOf = (ws: string) =>
      ws === 'clinical_doc' || ws === 'patient_engagement' ? 'clinical-ops'
      : ws === 'credentialing' ? 'hr' : ws;
    const TMPL_BY_TYPE = new Map(AGENT_TEMPLATES.map((t) => [t.type, t]));
    const byKey = new Map<string, any>();
    for (const key of enabledKeys) {
      const [portcoId, ws] = key.split('|');
      const ui = uiOf(ws);
      const k = `${portcoId}|${ui}`;
      if (byKey.has(k)) continue;
      const tmpl = TMPL_BY_TYPE.get(ui);
      byKey.set(k, {
        ...(tmpl ?? { name: ws, description: '', capabilities: [], kpis: [], recentRecommendations: [], version: 'v1', successRate: 0, actionsToday: 0, lastActionAt: '', workstream: ui, type: ui, status: 'idle' }),
        id: `agent_${ui}_${portcoId}`,
        portcoId,
        type: ui,
        workstream: ui,
        name: tmpl?.name || ws,
        status: agentEnabled[key] ? 'idle' : 'paused',
        lastActionAt: '',
        actionsToday: 0,
        successRate: tmpl?.successRate ?? 0,
      });
    }
    return [...byKey.values()];
  }, [configLoaded, agentEnabled]);

  // Filter by PortCo
  const portCoFilteredAgents = safeAgents.filter(
    (agent) => selectedPortco === 'all' || agent.portcoId === selectedPortco
  );

  // When "All Businesses" + "All Workstreams": show 8 aggregated cards
  // When "All Businesses" + specific workstream: show per-PortCo instances
  // When specific PortCo: show that PortCo's agents
  const isAggregatedView = selectedPortco === 'all' && selectedWorkstream === 'all';

  const displayAgents = useMemo(() => {
    if (isAggregatedView) {
      return aggregateByWorkstream(portCoFilteredAgents);
    }
    return portCoFilteredAgents.filter((agent) =>
      selectedWorkstream === 'all' ? true : agent.workstream === selectedWorkstream
    );
  }, [portCoFilteredAgents, selectedWorkstream, isAggregatedView]);

  // Derive workstream tabs from filtered agents
  const availableWorkstreams = Array.from(new Set(portCoFilteredAgents.map((a) => a.workstream)));
  const workstreamCounts = availableWorkstreams.reduce((acc, ws) => {
    acc[ws] = portCoFilteredAgents.filter((a) => a.workstream === ws).length;
    return acc;
  }, {} as Record<string, number>);

  const totalActionsToday = displayAgents.reduce((sum, a) => sum + (a.actionsToday || 0), 0);
  const avgSuccessRate = displayAgents.length
    ? (displayAgents.reduce((sum, a) => sum + (a.successRate || 0), 0) / displayAgents.length).toFixed(1)
    : '0.0';

  const handleSelectWorkstream = (ws: string) => {
    setSelectedWorkstream(ws);
    if (ws === 'all') {
      navigate('/workstreams');
    } else {
      navigate(`/workstreams/${ws}`);
    }
  };

  // Get PortCo name by id
  const getPortCoName = (id: string) => {
    const p = PORTCOS.find((p) => p.id === id);
    return p?.name || id;
  };

  return (
    <div className="space-y-6 pb-8">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <h1 className="text-xl font-bold text-slate-800 tracking-tight">
            {selectedPortco === 'all' ? 'Workstream Intelligence' : `${selectedPortcoName} • Workstreams`}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {selectedPortco === 'all'
              ? 'Monitor autonomous operational agents, performance KPIs, and recent recommendations across all PortCos'
              : `Monitor ${selectedPortcoName}'s agents, KPIs, and recommendations`}
          </p>
        </div>

        <button
          onClick={() => navigate('/audit')}
          className="self-start md:self-auto flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-medium shadow-sm transition-colors"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 text-mantis-600" />
          <span>Agent Logs</span>
        </button>
      </div>

      {/* WORKSTREAM FILTER TABS */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        <button
          onClick={() => handleSelectWorkstream('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
            selectedWorkstream === 'all'
              ? 'bg-mantis-600 text-white shadow-sm'
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          All Workstreams ({isAggregatedView ? availableWorkstreams.length : portCoFilteredAgents.length})
        </button>
        {availableWorkstreams.map((ws) => (
          <button
            key={ws}
            onClick={() => handleSelectWorkstream(ws)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors shrink-0 ${
              selectedWorkstream === ws
                ? 'bg-mantis-600 text-white shadow-sm'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {ws.replace('-', ' ')} ({workstreamCounts[ws] || 0})
          </button>
        ))}
      </div>

      {/* WORKSTREAM OVERVIEW KPI BAR */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 text-xs">
            <span>Active Agents</span>
            <Cpu className="w-4 h-4 text-mantis-600" />
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{displayAgents.length}</div>
          <p className="text-xs text-slate-500 mt-0.5">
            {isAggregatedView ? 'Unique workstream types' : selectedPortco === 'all' ? 'Across all PortCos' : `In ${selectedPortcoName}`}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 text-xs">
            <span>Actions Today</span>
            <Activity className="w-4 h-4 text-mantis-600" />
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{totalActionsToday}</div>
          <p className="text-xs text-mantis-600 font-medium mt-0.5">Automated workflow execution</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 text-xs">
            <span>Avg Success Rate</span>
            <ShieldCheck className="w-4 h-4 text-mantis-600" />
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-800">{avgSuccessRate}%</div>
          <p className="text-xs text-slate-500 mt-0.5">High precision guardrail compliance</p>
        </div>
      </div>

      {/* INTEGRATION SOURCES */}
      <section className="space-y-3">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <Plug className="w-4 h-4 text-mantis-600" />
          Integration Sources
        </h2>
        <div className="flex flex-wrap gap-2">
          {(WORKSTREAM_INTEGRATIONS[selectedWorkstream] || []).flatMap((intId: string) => {
            // Resolve this workstream's adapter to the selected portco's own
            // connection instances. In the aggregated view every business's
            // connection gets its own chip.
            const scoped = getIntegrationsForPortco(selectedPortco === 'all' ? 'all' : selectedPortco);
            const instances = scoped.filter((i) => i.templateId === intId);
            return instances.map((intConfig) => {
              const statusColor =
                intConfig.status === 'connected'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : intConfig.status === 'error'
                  ? 'bg-rose-50 text-rose-700 border-rose-200'
                  : intConfig.status === 'pending'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200';
              return (
                <div
                  key={intConfig.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${statusColor}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${intConfig.status === 'connected' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  <span className="font-semibold">{intConfig.name}</span>
                  <span className="text-[10px] opacity-70">
                    · {PORTCOS.find((p) => p.id === intConfig.portcoId)?.code ?? intConfig.portcoId}
                    {' '}&middot; {intConfig.category}
                  </span>
                </div>
              );
            });
          })}
          {(!WORKSTREAM_INTEGRATIONS[selectedWorkstream] || WORKSTREAM_INTEGRATIONS[selectedWorkstream].length === 0) && (
            <p className="text-xs text-slate-400">No integrations configured for this workstream.</p>
          )}
        </div>
      </section>

      {/* AGENT CARDS GRID */}
      <section className="space-y-4">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
          <Cpu className="w-4 h-4 text-mantis-600" />
          {isAggregatedView ? 'Workstream Types' : 'Workstream Agents'} ({displayAgents.length})
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {displayAgents.map((agent: any) => {
            const isExpanded = expandedAgentId === agent.id;
            const statusStyle = getStatusBadgeStyle(agent.status);
            const displayKpis = agent.kpis ? agent.kpis.slice(0, 2) : [];
            const displayCapabilities = agent.capabilities ? agent.capabilities.slice(0, 3) : [];
            const topRec = agent.recentRecommendations && agent.recentRecommendations[0];
            const portcoCount = agent.portcoCount || 1;
            const instances = agent.instances || [agent];

            return (
              <div
                key={agent.id}
                className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:border-slate-300 transition-colors flex flex-col justify-between space-y-4"
              >
                {/* Agent Header */}
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-bold text-slate-800 text-base">{agent.name}</h3>
                      <span className="inline-block text-xs font-medium text-slate-500 capitalize mt-0.5">
                        {agent.workstream.replace('-', ' ')} • {agent.version || 'v1.0'}
                      </span>
                    </div>

                    {/* Status badge */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusStyle.badge}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                      {statusStyle.label}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">{agent.description}</p>

                  {/* PortCo indicator for aggregated view */}
                  {isAggregatedView && portcoCount > 1 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <Building2 className="w-3 h-3 text-slate-400" />
                      <span className="text-[11px] text-slate-500 font-medium">
                        Running in {portcoCount} PortCos
                      </span>
                    </div>
                  )}

                  {/* Show PortCo name on per-instance cards (All Businesses + specific workstream) */}
                  {!isAggregatedView && selectedPortco === 'all' && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <Building2 className="w-3 h-3 text-mantis-500" />
                      <span className="text-[11px] text-slate-700 font-semibold">{getPortCoName(agent.portcoId)}</span>
                    </div>
                  )}
                </div>

                {/* 2 KPIs */}
                {displayKpis.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {displayKpis.map((kpi: any, idx: number) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <span className="text-[11px] text-slate-500 font-medium block">{kpi.label}</span>
                        <div className="flex items-center justify-between mt-1">
                          <span className="font-semibold text-slate-800 text-sm">{kpi.value}</span>
                          {kpi.trend && (
                            <span className={`flex items-center text-[10px] font-medium ${kpi.trend === 'up' ? 'text-mantis-600' : kpi.trend === 'down' ? 'text-redwood-500' : 'text-slate-400'}`}>
                              {kpi.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : kpi.trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : null}
                              {kpi.trend}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Capabilities */}
                {displayCapabilities.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {displayCapabilities.map((cap: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-slate-50 border border-slate-100 text-[10px] text-slate-600 font-medium"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                )}

                {/* Top Recommendation */}
                {topRec && (
                  <div className="bg-gradient-to-r from-mantis-50 to-transparent border-l-2 border-mantis-400 rounded-r-lg px-3 py-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Sparkles className="w-3.5 h-3.5 text-mantis-600 shrink-0" />
                          <span className="text-[11px] font-bold text-slate-700">{topRec.title}</span>
                        </div>
                        <span className="text-[10px] text-mantis-600 font-medium ml-5">{topRec.impact}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getPriorityBadgeStyle(topRec.priority)}`}>
                        {topRec.priority}
                      </span>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-1 text-[10px] text-slate-400">
                    <Zap className="w-3 h-3 text-amber-400" />
                    <span>{agent.actionsToday || 0} actions today</span>
                    <span className="mx-1">•</span>
                    <span>{agent.successRate || 0}% success</span>
                  </div>
                  <button
                    onClick={() => setExpandedAgentId(isExpanded ? null : agent.id)}
                    className="text-[11px] text-mantis-600 hover:text-mantis-700 font-semibold flex items-center gap-1 px-2.5 py-1 rounded-md bg-mantis-50 hover:bg-mantis-100 transition-colors"
                  >
                    <span>{isExpanded ? 'Hide Details' : 'View Details'}</span>
                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                </div>

                {/* EXPORTED INLINE DETAIL PANEL */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-4 bg-slate-50/80 -mx-5 -mb-5 p-5 rounded-b-xl">
                    {/* KPIs */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Agent KPIs</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {agent.kpis && agent.kpis.map((kpi: any, idx: number) => (
                          <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 shadow-xs">
                            <span className="text-[10px] text-slate-500 font-medium block">{kpi.label}</span>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs font-bold text-slate-800">{kpi.value}</span>
                              {kpi.trend && (
                                <span className={`flex items-center text-[10px] font-medium ${
                                  kpi.trend === 'up' ? 'text-mantis-600' : kpi.trend === 'down' ? 'text-redwood-500' : 'text-slate-400'
                                }`}>
                                  {kpi.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : kpi.trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : null}
                                  {kpi.trend}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Capabilities */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Capabilities</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {agent.capabilities && agent.capabilities.map((cap: string, idx: number) => (
                          <span key={idx} className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] text-slate-700 font-medium">
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Recommendations */}
                    {agent.recentRecommendations && agent.recentRecommendations.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Recent Recommendations</h4>
                        <div className="space-y-2">
                          {agent.recentRecommendations.map((rec: any, idx: number) => (
                            <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 flex items-start justify-between gap-2 shadow-xs">
                              <div>
                                <p className="text-xs font-semibold text-slate-800">{rec.title}</p>
                                <p className="text-[11px] text-mantis-600 font-medium mt-0.5">{rec.impact}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getPriorityBadgeStyle(rec.priority)} shrink-0 capitalize`}>
                                {rec.priority}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recent Actions Timeline */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Recent Actions Timeline</h4>
                      <div className="space-y-2 bg-white p-3 rounded-lg border border-slate-200 shadow-xs">
                        {getMockRecentActions(agent.workstream, agent.name).map((act, idx) => (
                          <div key={idx} className="flex items-start gap-2.5 text-xs pb-2 border-b border-slate-100 last:border-0 last:pb-0">
                            <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${
                              act.status === 'Completed' || act.status === 'Approved' ? 'bg-mantis-500' :
                              act.status === 'Processing' || act.status === 'Executing' ? 'bg-blue-500' : 'bg-amber-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-slate-800">{act.description}</span>
                                <span className="text-[10px] text-slate-400 shrink-0">{act.timestamp}</span>
                              </div>
                              <span className="text-[10px] text-slate-500 font-medium">{act.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Per-Tenant On/Off Switch + Manual Run */}
                    <div className="pt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          role="switch"
                          aria-checked={agentEnabled[`${agent.portcoId}|${agent.workstream}`] ?? true}
                          onClick={() =>
                            handleToggleAgent(
                              agent.id,
                              agent.portcoId,
                              agent.workstream,
                              !(agentEnabled[`${agent.portcoId}|${agent.workstream}`] ?? true)
                            )
                          }
                          disabled={!configLoaded}
                          title={configLoaded ? 'Toggle this agent for this tenant' : 'Loading config…'}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 cursor-pointer disabled:opacity-40 ${
                            (agentEnabled[`${agent.portcoId}|${agent.workstream}`] ?? true)
                              ? 'bg-mantis-500'
                              : 'bg-slate-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                              (agentEnabled[`${agent.portcoId}|${agent.workstream}`] ?? true)
                                ? 'translate-x-[18px]'
                                : 'translate-x-[3px]'
                            }`}
                          />
                        </button>
                        <span className="text-[11px] text-slate-500">
                          {(agentEnabled[`${agent.portcoId}|${agent.workstream}`] ?? true) ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRunAgent(agent.id, agent.portcoId, agent.workstream)}
                        disabled={runningAgents[agent.id] || !(agentEnabled[`${agent.portcoId}|${agent.workstream}`] ?? true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-mantis-600 hover:bg-mantis-500 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {runningAgents[agent.id] ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Executing...</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-3.5 h-3.5 fill-current" />
                            <span>Run Agent</span>
                          </>
                        )}
                      </button>
                    </div>
                    {runStatus[agent.id] && (
                      <div className="text-[11px] text-mantis-700 font-medium bg-mantis-50 p-2 rounded-lg border border-mantis-200 mt-2">
                        {runStatus[agent.id]}
                      </div>
                    )}
                  </div>
                )}
                </div>
            );
          })}
        </div>

        {/* RCM LIVE DATA SECTION — Real claims from SETWorks */}
        {selectedWorkstream === "rcm" && (
          <ErrorBoundary>
            <RcmLiveDataSection portcoId={selectedPortco} portcoName={selectedPortcoName} />
          </ErrorBoundary>
        )}

        {/* Empty State */}
        {displayAgents.length === 0 && (
          <div className="text-center py-16">
            <Cpu className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-500">No agents found for this selection</p>
            <p className="text-xs text-slate-400 mt-1">
              {selectedPortco !== 'all'
                ? `${selectedPortcoName} may not have agents in this workstream`
                : 'Try selecting a different workstream filter'}
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

// ===== RCM Live Data Section =====

const RcmLiveDataSection: React.FC<{ portcoId: string; portcoName: string }> = ({ portcoId, portcoName }) => {
  const [claims, setClaims] = useState<RcmClaimsResponse | null>(null);
  const [dashboard, setDashboard] = useState<RcmDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const [claimsRes, dashRes] = await Promise.all([
        rcmApi.getClaims(portcoId),
        rcmApi.getDashboard(portcoId),
      ]);
      if (!cancelled) {
        setClaims(claimsRes);
        setDashboard(dashRes);
        setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [portcoId]);

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    const res = await rcmApi.triggerAgentRun(portcoId, 'rcm');
    if (res) {
      setTriggerResult(`✓ Agent run triggered: ${res.runId} — ${res.claimsQueued} claims queued for analysis`);
      setTimeout(async () => {
        const [claimsRes, dashRes] = await Promise.all([
          rcmApi.getClaims(portcoId),
          rcmApi.getDashboard(portcoId),
        ]);
        setClaims(claimsRes);
        setDashboard(dashRes);
      }, 3000);
    } else {
      setTriggerResult('Failed to trigger agent run. Check function app logs.');
    }
    setTriggering(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading live RCM data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* RCM Header with trigger button */}
      <div className="bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <h3 className="text-sm font-bold text-slate-800">RCM Agent — Live Denial Analysis</h3>
              <p className="text-xs text-slate-500">
                {dashboard ? `${(dashboard?.metrics?.deniedClaims || 0)} denied claims • $${(dashboard.metrics.totalDenialAmount || 0).toLocaleString()} at risk` : 'Loading...'}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">Source: SETWorks EHR (CSV exchange)</p>
            </div>
          </div>
          <button
            onClick={handleTrigger}
            disabled={triggering}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
          >
            {triggering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            <span>{triggering ? 'Triggering...' : 'Trigger Agent Run'}</span>
          </button>
        </div>
        {triggerResult && (
          <div className="mt-3 px-3 py-2 bg-mantis-50 border border-mantis-200 rounded-lg text-xs text-mantis-700">
            {triggerResult}
          </div>
        )}
      </div>

      {/* RCM KPIs — Live from API */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {dashboard.kpis.slice(0, 4).map((kpi) => (
            <div key={kpi.id} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
              <div className="text-xs text-slate-500">{kpi.label}</div>
              <div className="mt-1 text-lg font-bold text-slate-800">{kpi.value}</div>
              <div className={`text-[10px] font-medium ${kpi.trend === 'down' ? 'text-mantis-600' : kpi.trend === 'up' ? 'text-redwood-500' : 'text-slate-400'}`}>
                {kpi.trendLabel}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Claims Table — Live from API */}
      {claims && claims.claims.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700">Denied Claims ({claims.count})</h3>
            <p className="text-xs text-slate-500">Live claims data from SETWorks EHR</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Patient</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Claim ID</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">CPT</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-500">Amount</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Denial Reason</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Payer</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-500">Appeal Deadline</th>
                </tr>
              </thead>
              <tbody>
                {claims.claims.map((claim) => (
                  <tr key={claim.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 font-medium text-slate-700">{claim.patientName}</td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono">{claim.claimId}</td>
                    <td className="px-4 py-2.5 text-slate-500">{claim.cptCode}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-700">${(claim.claimAmount || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-100 text-[10px] font-medium">
                        {claim.denialCode}
                      </span>
                      <span className="ml-1.5">{claim.denialReason}</span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{claim.payerName}</td>
                    <td className="px-4 py-2.5 text-slate-500">{claim.appealDeadline}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-100">
                <tr>
                  <td className="px-4 py-2.5 font-bold text-slate-700" colSpan={3}>Total at Risk</td>
                  <td className="px-4 py-2.5 text-right font-bold text-redwood-500">${(claims.totalAmount || 0).toLocaleString()}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Agent Run History — Live from API */}
      {dashboard && dashboard.recentRuns && dashboard.recentRuns.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700">Agent Run History</h3>
          </div>
          <div className="divide-y divide-slate-50">
            {dashboard.recentRuns.map((run) => (
              <div key={run.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${run.status === 'completed' ? 'bg-mantis-500' : 'bg-amber-500'}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-700">{run.agentName}</p>
                    <p className="text-xs text-slate-500">{run.summary}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{run.claimsAnalyzed} claims</span>
                  <span className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />{run.denialsFound} denials</span>
                  <span className="flex items-center gap-1 text-mantis-600 font-medium"><DollarSign className="w-3 h-3" />{((run.avgConfidence || 0) * 100).toFixed(0)}% conf</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(run.startTime).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
